import { ParameterId as Api, VerbId as ApiVerbId } from '@tableau/api-internal-contract-js';
import { DocCommands as PresLayerDocCommandId, SrvCommands as PresLayerSrvCommandId } from '../../gen-src/preslayer/CommandIds';
import { ParameterId as PL } from '../../gen-src/preslayer/Params';
import { CommandMappingRegistry } from './CommandMappingRegistry';
import { CommandMappingRegistryFactory } from './CommandMappingRegistryFactory';
import { CommandNamespace, CommandParameter, CommandRegistration } from './CommandRegistration';

export class EmbeddingCommandMappingRegistryFactory {
  /**
   * Creates and populates a new CommandMappingRegistry for Embedding
   *
   * @static
   * @returns {CommandMappingRegistry}
   * @memberof EmbeddingCommandMappingRegistryFactory
   */
  public static createCommandMappingRegistry(): CommandMappingRegistry {
    let result: CommandMappingRegistry = CommandMappingRegistryFactory.createCommandMappingRegistry();

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetEmbeddingClientInfo,
        PresLayerDocCommandId.GetEmbeddingClientInfo,
        [],
        [new CommandParameter(Api.EmbeddingBootstrapInfo, PL.EmbeddingApiBootstrap)],
      ),
    );

    result.addCommand(
      new CommandRegistration(ApiVerbId.GetCurrentSrc, PresLayerDocCommandId.GetCurrentSrc, [], [new CommandParameter(Api.Url, PL.URL)]),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.ActivateSheet,
        PresLayerSrvCommandId.EnsureLayoutForSheetServer,
        [new CommandParameter(Api.SwitchToSheetName, PL.TargetSheet)],
        undefined,
        CommandNamespace.TabSrv,
      ),
    );

    result.addCommand(
      new CommandRegistration(ApiVerbId.RemoveExternalMenuItem, PresLayerDocCommandId.WebRemoveExternalContextMenu, [
        new CommandParameter(Api.WorksheetName, PL.Worksheet),
        new CommandParameter(Api.ExternalMenuItemId, PL.ExternalMenuItemId),
      ]),
    );

    result.addCommand(
      new CommandRegistration(ApiVerbId.ExecuteExternalMenuItem, PresLayerDocCommandId.WebExecuteExternalContextMenu, [
        new CommandParameter(Api.WorksheetName, PL.Worksheet),
        new CommandParameter(Api.ExternalMenuItemId, PL.ExternalMenuItemId),
      ]),
    );

    result.addCommand(
      new CommandRegistration(ApiVerbId.RenameExternalMenu, PresLayerDocCommandId.WebRenameExternalContextMenu, [
        new CommandParameter(Api.WorksheetName, PL.Worksheet),
        new CommandParameter(Api.ExternalMenuHeader, PL.ExternalMenuHeader),
        new CommandParameter(Api.ExternalMenuDescription, PL.ExternalMenuDescription),
      ]),
    );

    result.addCommand(
      new CommandRegistration(ApiVerbId.SetSheetSize, PresLayerDocCommandId.SetSheetSize, [
        new CommandParameter(Api.SheetName, PL.Sheet),
        new CommandParameter(Api.MinWidth, PL.MinWidth),
        new CommandParameter(Api.MinHeight, PL.MinHeight),
        new CommandParameter(Api.MaxWidth, PL.MaxWidth),
        new CommandParameter(Api.MaxHeight, PL.MaxHeight),
      ]),
    );

    result.addCommand(
      new CommandRegistration(ApiVerbId.RevertWorkbook, PresLayerSrvCommandId.RevertWorkbookServer, [], undefined, CommandNamespace.TabSrv),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.AppendExternalMenuItem,
        PresLayerDocCommandId.WebAppendExternalContextMenu,
        [
          new CommandParameter(Api.WorksheetName, PL.Worksheet),
          new CommandParameter(Api.ExternalMenuItemDisplayName, PL.ExternalMenuItemDisplayName),
        ],
        [new CommandParameter(Api.ExternalMenuItemId, PL.ExternalMenuItemId)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.UpdateCustomView,
        PresLayerDocCommandId.UpdateCustomView,
        [
          new CommandParameter(Api.CustomViewLuid, PL.CustomViewLuid),
          new CommandParameter(Api.CustomViewName, PL.CustomViewName),
          new CommandParameter(Api.CustomViewIsDefault, PL.CustomViewIsDefault),
          new CommandParameter(Api.CustomViewIsPublic, PL.CustomViewIsPublic),
        ],
        [new CommandParameter(Api.CustomView, PL.EmbeddingCustomView)],
        CommandNamespace.TabDoc,
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetCustomViews,
        PresLayerDocCommandId.GetCustomViews,
        [],
        [new CommandParameter(Api.CustomViews, PL.EmbeddingCustomViewList)],
        CommandNamespace.TabDoc,
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.ShowCustomView,
        PresLayerDocCommandId.ShowCustomView,
        [new CommandParameter(Api.CustomViewName, PL.CustomViewName)],
        [new CommandParameter(Api.CustomView, PL.EmbeddingCustomView)],
        CommandNamespace.TabDoc,
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.RemoveCustomView,
        PresLayerDocCommandId.RemoveCustomView,
        [new CommandParameter(Api.CustomViewName, PL.CustomViewName)],
        [new CommandParameter(Api.CustomView, PL.EmbeddingCustomView)],
        CommandNamespace.TabDoc,
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.SaveWorkbookAsCustomView,
        PresLayerDocCommandId.SaveWorkbookAsCustomView,
        [
          new CommandParameter(Api.CustomViewName, PL.CustomViewName),
          new CommandParameter(Api.CustomViewIsDefault, PL.CustomViewIsDefault),
        ],
        [new CommandParameter(Api.CustomView, PL.EmbeddingCustomView)],
        CommandNamespace.TabDoc,
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.SetActiveCustomViewAsDefault,
        PresLayerDocCommandId.SetActiveCustomViewAsDefault,
        [],
        [],
        CommandNamespace.TabDoc,
      ),
    );

    result.addCommand(new CommandRegistration(ApiVerbId.ExportImage, PresLayerDocCommandId.WebExportPng, []));

    result.addCommand(new CommandRegistration(ApiVerbId.DownloadWorkbook, PresLayerDocCommandId.ExportWorkbookViewMode, []));

    result.addCommand(new CommandRegistration(ApiVerbId.ExportPdf, PresLayerDocCommandId.WebExportPdf, []));

    result.addCommand(new CommandRegistration(ApiVerbId.ExportPowerpoint, PresLayerDocCommandId.WebExportPowerPoint, []));

    result.addCommand(new CommandRegistration(ApiVerbId.ExportData, PresLayerDocCommandId.WebExportData, []));

    result.addCommand(new CommandRegistration(ApiVerbId.ExportCrosstab, PresLayerDocCommandId.WebExportCrossTab, []));

    result.addCommand(new CommandRegistration(ApiVerbId.Share, PresLayerDocCommandId.ShowShareUI, []));

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.SetAutoUpdate,
        PresLayerSrvCommandId.SetAutoUpdateServer,
        [new CommandParameter(Api.State, PL.State)],
        undefined,
        CommandNamespace.TabSrv,
      ),
    );

    result.addCommand(new CommandRegistration(ApiVerbId.Undo, PresLayerDocCommandId.Undo, []));

    result.addCommand(new CommandRegistration(ApiVerbId.Redo, PresLayerDocCommandId.Redo, []));

    result.addCommand(new CommandRegistration(ApiVerbId.ActivateNextStoryPoint, PresLayerDocCommandId.ActivateNextStoryPoint, [], []));

    result.addCommand(
      new CommandRegistration(ApiVerbId.ActivatePreviousStoryPoint, PresLayerDocCommandId.ActivatePreviousStoryPoint, [], []),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.ActivateStoryPoint,
        PresLayerDocCommandId.ActivateStoryPoint,
        [new CommandParameter(Api.StoryPointIndex, PL.Index)],
        [],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.RevertStoryPoint,
        PresLayerDocCommandId.RevertIndexedStoryPoint,
        [new CommandParameter(Api.StoryPointIndex, PL.Index)],
        [new CommandParameter(Api.StoryPointInfo, PL.EmbeddingStoryPoint)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetEmbeddingAbstractQueryWithDataModel,
        PresLayerDocCommandId.GetEmbeddingAbstractQueryWithDataModel,
        [],
        [new CommandParameter(Api.EmbeddingAbstractQuery, PL.EmbeddingAbstractQueryWithDataModel)],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.GetVizStateWithDataModel,
        PresLayerDocCommandId.GetVizStateWithDataModel,
        [],
        [
          new CommandParameter(Api.VizAPIStateWithDataModel, PL.VizStateWithDataModel),
          CommandParameter.makeOptional(Api.VizAPIStateWarningMsg, PL.VizStateWarningMsg),
        ],
      ),
    );

    result.addCommand(
      new CommandRegistration(
        ApiVerbId.SetVizStateWithDataModel,
        PresLayerDocCommandId.SetVizStateWithDataModel,
        [new CommandParameter(Api.VizAPIStateWithDataModel, PL.VizStateWithDataModel)],
        [CommandParameter.makeOptional(Api.VizAPIStateWarningMsg, PL.VizStateWarningMsg)],
      ),
    );

    return result;
  }
}
